/**
 * @file        CaseModals.jsx
 * @module      modules/kyb/manager/console
 * @description The case actions from the shared HTML design: request
 *              documents, override a check, approve, reject, move back to
 *              review, reopen — plus the document viewer. Each validates like
 *              the design, then calls the backend; the backend re-checks.
 */

import React, { useEffect, useState } from "react";
import { kybManagerApi } from "../api/kybManagerApi";
import { CHECK_KEYS, CHECK_NAMES, CS, Modal, useToast } from "./ui";

const OVERRIDE = [
  ["verified", "Passed"],
  ["review_required", "Needs review"],
  ["not_matched", "Failed"],
];
const RAW_TO_UI = { verified: "verified", review_required: "review", not_matched: "nomatch" };

function useSubmit(onDone) {
  const toast = useToast();
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn, okMsg) => {
    setBusy(true);
    setErr("");
    try {
      const res = await fn();
      toast(typeof okMsg === "function" ? okMsg(res) : okMsg);
      onDone(res);
    } catch (e) {
      setErr(e.message || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };
  return { err, setErr, busy, run };
}

const Err = ({ children }) =>
  children ? (
    <p className="err" role="alert">
      {children}
    </p>
  ) : null;

const emailed = (res, ok) => (res && res.emailed === false ? `${ok}. The e-mail to the Customer couldn't be sent` : `${ok}. The Customer has been emailed`);

const PLACEHOLDER = {
  "Board resolution": "e.g. “A signed copy on company letterhead, authorizing the applicant.”",
  "Authorization letter": "e.g. “Signed by a partner or the proprietor, naming the applicant.”",
  "Address proof of business": "e.g. “A utility bill or rent agreement from the last 3 months.”",
  "Other (describe in message)": "Describe the document you need and why.",
};

/**
 * Request documents — one row per document. Ticking a document opens its own
 * message to the Customer and its own "respond within"; each row is sent as
 * { doc, message, dueAt } — dueAt is a date and time (to the minute) the
 * team picks; if it passes without the Customer submitting, the case goes
 * back to Needs review.
 */
// <input type="datetime-local"> works in local time, "YYYY-MM-DDTHH:mm" (no seconds).
const pad2 = (n) => String(n).padStart(2, "0");
const toLocalInput = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
const inDays = (n) => {
  const d = new Date(Date.now() + n * 24 * 3600e3);
  d.setSeconds(0, 0);
  return d;
};
const fmtDue = (v) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: true });
};

export function DocsModal({ c, onClose, onDone }) {
  const approved = c.status === "auto" || c.status === "manual";
  const [rows, setRows] = useState({}); // doc → { message, due: "YYYY-MM-DDTHH:mm" }
  const { err, setErr, busy, run } = useSubmit(onDone);
  const picked = c.docTypes.filter((d) => rows[d]);

  const toggle = (d, on) =>
    setRows((x) => {
      const n = { ...x };
      if (on) n[d] = { message: "", due: toLocalInput(inDays(3)) };
      else delete n[d];
      return n;
    });
  const setRow = (d, k, v) => setRows((x) => ({ ...x, [d]: { ...x[d], [k]: v } }));

  const submit = () => {
    if (!picked.length) return setErr("Pick at least one document.");
    const missing = picked.find((d) => !rows[d].message.trim());
    if (missing) return setErr(`Write a message for “${missing}” so the Customer knows what's needed.`);
    for (const d of picked) {
      const due = new Date(rows[d].due);
      if (!rows[d].due || Number.isNaN(due.getTime())) return setErr(`Pick a date and time for “${d}”.`);
      if (due.getTime() <= Date.now()) return setErr(`The deadline for “${d}” must be in the future.`);
      if (due.getTime() > inDays(60).getTime()) return setErr(`The deadline for “${d}” can be at most 60 days away.`);
    }
    const items = picked.map((d) => ({ doc: d, message: rows[d].message.trim(), dueAt: new Date(rows[d].due).toISOString() }));
    return run(() => kybManagerApi.requestDocuments(c.id, { items }), (r) => emailed(r, "Request sent"));
  };

  return (
    <Modal
      title="Request documents"
      sub={`Tick each document you need and tell ${c.person.name || "the Customer"} what's needed and by when. We'll email them a link to upload. The SLA pauses until they respond; if a deadline passes, the case goes back to Needs review.`}
      onClose={onClose}
      wide
      footer={
        <>
          <span className="reqcount">{picked.length ? `${picked.length} document${picked.length > 1 ? "s" : ""} selected` : ""}</span>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={submit} disabled={busy}>
            Send request
          </button>
        </>
      }
    >
      <div className="reqrows" role="list" aria-label="Documents">
        {c.docTypes.map((d, i) => {
          const row = rows[d];
          const id = `req-${i}`;
          return (
            <div key={d} className={`reqrow${row ? " on" : ""}`} role="listitem">
              <label className="reqhead">
                <input type="checkbox" checked={!!row} onChange={(e) => toggle(d, e.target.checked)} />
                <span>{d}</span>
              </label>
              {row ? (
                <div className="reqbody">
                  <div className="fld">
                    <label htmlFor={`${id}-msg`}>Message to the Customer</label>
                    <textarea
                      id={`${id}-msg`}
                      autoFocus
                      value={row.message}
                      onChange={(e) => setRow(d, "message", e.target.value)}
                      placeholder={PLACEHOLDER[d] || "Explain what's needed and why."}
                      maxLength={1000}
                    />
                  </div>
                  <div className="fld">
                    <label htmlFor={`${id}-due`}>Upload by</label>
                    <input
                      id={`${id}-due`}
                      type="datetime-local"
                      step="60"
                      min={toLocalInput(new Date())}
                      max={toLocalInput(inDays(60))}
                      value={row.due}
                      onChange={(e) => setRow(d, "due", e.target.value)}
                    />
                    <small className="hint">{row.due ? fmtDue(row.due) : "Pick a date and time"}</small>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {approved ? (
        <div className="banner warn" style={{ margin: "12px 0 0" }}>
          <div>
            <b>This business is approved</b>Requesting documents moves it to Awaiting Customer and pauses bookings until it's approved again.
          </div>
        </div>
      ) : null}
      <Err>{err}</Err>
    </Modal>
  );
}

export function OverrideModal({ c, check, onClose, onDone }) {
  const cur = CS[c.checks[check].status] || CS.pending;
  const [v, setV] = useState(OVERRIDE.find(([k]) => RAW_TO_UI[k] === c.checks[check].status)?.[0] || "verified");
  const [reason, setReason] = useState("");
  const { err, setErr, busy, run } = useSubmit(onDone);
  const submit = () => {
    if (!reason.trim()) return setErr("Add a reason for the override.");
    return run(() => kybManagerApi.override(c.id, { check, status: v, reason: reason.trim() }), `${CHECK_NAMES[check]} set to ${OVERRIDE.find(([k]) => k === v)[1]}`);
  };
  return (
    <Modal
      title={`Override: ${CHECK_NAMES[check]}`}
      sub={`Currently ${cur[2]}. Your change and reason are recorded in the activity log.`}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={submit} disabled={busy}>
            Save override
          </button>
        </>
      }
    >
      <div className="fld">
        <label>New status</label>
        <div className="radios">
          {OVERRIDE.map(([k, label]) => (
            <label key={k}>
              <input type="radio" name="ov" value={k} checked={v === k} onChange={() => setV(k)} />
              {label}
            </label>
          ))}
        </div>
      </div>
      <div className="fld">
        <label htmlFor="mReason">Reason</label>
        <textarea id="mReason" autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What did you check? e.g. “LLP agreement lists her as a designated partner.”" />
        <div className="hint">Required. Visible to the KYC team only.</div>
      </div>
      <Err>{err}</Err>
    </Modal>
  );
}

export function ApproveModal({ c, onClose, onDone }) {
  const open = CHECK_KEYS.filter((k) => c.checks[k].raw !== "verified");
  const [reason, setReason] = useState("");
  const { err, setErr, busy, run } = useSubmit(onDone);
  const submit = () => {
    if (open.length && !reason.trim()) return setErr("Add a reason, because some checks haven't passed.");
    return run(() => kybManagerApi.approve(c.id, { reason: reason.trim() || undefined }), (r) => emailed(r, "Business approved"));
  };
  return (
    <Modal
      title="Approve this business"
      sub={`${c.person.name || "The Customer"} becomes the admin for ${c.business.name || "this business"} and can book straight away. We'll email them.`}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={submit} disabled={busy}>
            Approve business
          </button>
        </>
      }
    >
      {open.length ? (
        <>
          <div className="banner warn" style={{ margin: "0 0 14px" }}>
            <div>
              <b>
                {open.length} check{open.length > 1 ? "s haven't" : " hasn't"} passed
              </b>
              Approving marks {open.map((k) => CHECK_NAMES[k]).join(", ")} as Passed by you.
            </div>
          </div>
          <div className="fld">
            <label htmlFor="mReason">Reason</label>
            <textarea id="mReason" autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What did you verify? e.g. “Signed board resolution checked against MCA director list.”" />
            <div className="hint">Required when approving checks that haven't passed.</div>
          </div>
        </>
      ) : (
        <p style={{ margin: 0 }}>All four checks have passed.</p>
      )}
      <Err>{err}</Err>
    </Modal>
  );
}

export function RejectModal({ c, onClose, onDone }) {
  const [cat, setCat] = useState("");
  const [msg, setMsg] = useState("");
  const [note, setNote] = useState("");
  const { err, setErr, busy, run } = useSubmit(onDone);
  const submit = () => {
    if (!cat) return setErr("Choose a reason.");
    if (!msg.trim()) return setErr("Write a message for the Customer.");
    return run(() => kybManagerApi.reject(c.id, { category: cat, message: msg.trim(), note: note.trim() || undefined }), (r) => emailed(r, "Business rejected"));
  };
  return (
    <Modal
      title="Reject this business"
      sub={`We'll email ${c.person.name || "the Customer"} with the reason. They can start a new verification later.`}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn danger" onClick={submit} disabled={busy}>
            Reject business
          </button>
        </>
      }
    >
      <div className="fld">
        <label htmlFor="mCat">Reason</label>
        <select id="mCat" autoFocus value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">Choose a reason</option>
          {c.rejectCategories.map((x) => (
            <option key={x}>{x}</option>
          ))}
        </select>
      </div>
      <div className="fld">
        <label htmlFor="mMsg">Message to the Customer</label>
        <textarea
          id="mMsg"
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          placeholder="Say what's wrong and what they can do, e.g. “Your company is marked struck off on MCA, so we can't verify it.”"
        />
      </div>
      <div className="fld">
        <label htmlFor="mNote">Internal note (optional)</label>
        <textarea id="mNote" style={{ minHeight: 60 }} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <Err>{err}</Err>
    </Modal>
  );
}

export function PendingModal({ c, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const { err, setErr, busy, run } = useSubmit(onDone);
  const submit = () => {
    if (!reason.trim()) return setErr("Add a reason.");
    return run(() => kybManagerApi.moveToReview(c.id, { reason: reason.trim(), notify }), "Moved back to review. Bookings paused");
  };
  return (
    <Modal
      title="Move back to review"
      sub="Bookings for this business pause until it's approved again. The case is assigned to you with a new SLA."
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={submit} disabled={busy}>
            Move to review
          </button>
        </>
      }
    >
      <div className="fld">
        <label htmlFor="mReason">Reason</label>
        <textarea id="mReason" autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why does this need another look? e.g. “GST status changed to Cancelled since approval.”" />
        <div className="hint">Required. Visible to the KYC team only.</div>
      </div>
      <div className="checks" style={{ gridTemplateColumns: "1fr" }}>
        <label>
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
          Email {c.person.name || "the Customer"} that their verification is being reviewed again
        </label>
      </div>
      <Err>{err}</Err>
    </Modal>
  );
}

export function ReopenModal({ c, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const { err, setErr, busy, run } = useSubmit(onDone);
  const submit = () => {
    if (!reason.trim()) return setErr("Add a reason.");
    return run(() => kybManagerApi.reopen(c.id, { reason: reason.trim() }), "Case reopened");
  };
  return (
    <Modal
      title="Reopen this case"
      sub="The case goes back to Needs review, assigned to you with a new SLA."
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={submit} disabled={busy}>
            Reopen case
          </button>
        </>
      }
    >
      <div className="fld">
        <label htmlFor="mReason">Reason</label>
        <textarea id="mReason" autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. “Customer has restored the company on MCA.”" />
      </div>
      <Err>{err}</Err>
    </Modal>
  );
}

/** Opens an uploaded file from a blob (sent with the session cookie). */
export function DocViewer({ doc, onClose }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let made = null;
    kybManagerApi
      .file(doc.id)
      .then((blob) => {
        made = URL.createObjectURL(blob);
        setUrl(made);
      })
      .catch((e) => setErr(e.message || "Couldn't open the file."));
    return () => made && URL.revokeObjectURL(made);
  }, [doc.id]);
  const isImage = (doc.mimeType || "").startsWith("image/");
  return (
    <Modal
      title={doc.label}
      sub={doc.name}
      wide
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Close
          </button>
          <button className="btn primary" onClick={() => downloadDoc(doc)}>
            Download
          </button>
        </>
      }
    >
      {err ? <Err>{err}</Err> : !url ? <div className="loading">Loading…</div> : isImage ? <img src={url} alt={doc.name || doc.label} style={{ maxWidth: "100%", display: "block", margin: "0 auto" }} /> : <iframe className="viewer" title={doc.name || doc.label} src={url} />}
    </Modal>
  );
}

export async function downloadDoc(doc) {
  const blob = await kybManagerApi.file(doc.id, { download: true });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = doc.name || "document";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
